// PrivateMessageSystem.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './PrivateMessageSystem.css';

const PrivateMessageSystem = (props) => {

  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('inbox');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isComposing, setIsComposing] = useState(false);
  const [showMobileReader, setShowMobileReader] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fadingOutMessageId, setFadingOutMessageId] = useState(null);
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [scripts, setScripts] = useState([]);

  // --- Utilities -----------------------------------------------------------
  // The API historically returns `date` in different formats (epoch seconds,
  // epoch milliseconds, or a parsable datetime string). Normalize everything
  // to epoch milliseconds so sorting + display are correct.
  const normalizeEpochToMs = (value) => {
    if (value === null || value === undefined) return value;

    // Numeric or numeric-string epoch
    const asNumber = typeof value === 'number' ? value : (String(value).match(/^\d+$/) ? Number(value) : NaN);
    if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
      // If it's clearly seconds (10 digits or less), convert to ms.
      // Unix timestamps in seconds are typically 10 digits (e.g., 1707598800 for Feb 2024)
      // Unix timestamps in milliseconds are typically 13 digits (e.g., 1707598800000)
      // Use 1e12 as threshold: anything less is assumed to be seconds
      if (asNumber < 1e12) {
        return asNumber * 1000;
      }
      return asNumber;
    }

    // Datetime string
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      if (!Number.isNaN(parsed)) return parsed;
    }

    return value;
  };

  const normalizeMessage = (msg) => {
    if (!msg || typeof msg !== 'object') return msg;
    return {
      ...msg,
      date: normalizeEpochToMs(msg.date),
    };
  };

  const normalizeMessages = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map(normalizeMessage);
  };

  // Replace messages belonging to a specific folder, while keeping other
  // folder messages intact. This prevents the "Sent" tab from going empty
  // after refreshing Inbox-only data.
  const replaceMessagesForFolder = (prevMessages, folder, incomingMessages) => {
    const prev = Array.isArray(prevMessages) ? prevMessages : [];
    const incoming = Array.isArray(incomingMessages) ? incomingMessages : [];

    const isInboxMsg = (m) => parseInt(m.recip) === parseInt(props.currentUserId) && parseInt(m.sent) === 1;
    const isSentMsg = (m) => parseInt(m.sender) === parseInt(props.currentUserId) && parseInt(m.sent) === 1;

    let keepPredicate = () => true;
    if (folder === 'inbox') {
      keepPredicate = (m) => !isInboxMsg(m);
    } else if (folder === 'sent') {
      keepPredicate = (m) => !isSentMsg(m);
    }

    const kept = prev.filter(keepPredicate);
    const merged = [...kept, ...incoming];

    // De-dupe by message id (mid) while preserving the newest copy.
    // Also ensure all messages are normalized (especially dates)
    const byId = new Map();
    for (const m of merged) {
      const key = m?.mid != null ? String(m.mid) : JSON.stringify(m);
      byId.set(key, normalizeMessage(m));
    }
    return Array.from(byId.values());
  };


  useEffect(() => {

    getUsers();
    getMessages();
    getUserScripts();

  }, []);

  // Persist the *combined* messages list to cache. (We merge inbox+sent into
  // one array so folder switching works without losing the other folder.)
  useEffect(() => {
    try {
      localStorage.setItem('privateMessages', JSON.stringify(messages || []));
    } catch (e) {
      // Ignore quota / storage errors.
      console.warn('Failed to cache privateMessages:', e);
    }
  }, [messages]);



  const getUsers = async (obj) => {
    try {
      const manageLoading = obj?.manageLoading !== false;
      if (manageLoading) setLoading(true);


      const result = await axios.get("/api/messages/message_api", {
          params: { _t: Date.now(), endpoint:"users" }
      });
      console.log("getUsers()");
      console.log(result.data)
      setUsers(result.data.users)
      
      localStorage.setItem('privateUsers', JSON.stringify( result.data.users ));
    }
    catch (e) {
      console.log(e)
    }
    finally{
      const manageLoading = obj?.manageLoading !== false;
      if (manageLoading) setLoading(false); // Stop loading after any error
    }
  }

  const getUserScripts = async (obj) => {
    try {
      const manageLoading = obj?.manageLoading !== false;
      if (manageLoading) setLoading(true);
      const result = await axios.get("/api/messages/scripts", {
          params: { _t: Date.now(), endpoint:"not-applicable" }
      });      
      console.log("getUserScripts()");
      console.log(result.data)
      setScripts(result.data?.scripts)
      localStorage.setItem('userScripts', JSON.stringify( result.data?.scripts ));
    }
    catch (e) {
      console.log(e)
    }
    finally{
      const manageLoading = obj?.manageLoading !== false;
      if (manageLoading) setLoading(false); // Stop loading after any error
    }
  }  

  const getMessages = async (obj) => {
    try {
      const folderToFetch = obj?.folder || selectedFolder;
      const manageLoading = obj?.manageLoading !== false;

      console.log("folderToFetch: " + folderToFetch)
      if (manageLoading) setLoading(true);
      //const request = "https://fdxva.org/api/messages/message_api.php?endpoint=messages&folder=" + folderToFetch + "&rnd=" + Math.random();
      //const result = await axios(request);

      const result = await axios.get("/api/messages/message_api", {
        params: { _t: Date.now(), endpoint:"messages", folder: folderToFetch}
      });      
      console.log("getMessages()");
      console.log(result.data)
      const normalizedIncoming = normalizeMessages(result.data?.messages);

      // Merge into our combined cache instead of overwriting the other folder.
      setMessages(prev => replaceMessagesForFolder(prev, folderToFetch, normalizedIncoming));
    }
    catch (e) {
      console.log(e)
    }
    finally{
      const manageLoading = obj?.manageLoading !== false;
      if (manageLoading) setLoading(false); // Stop loading after any error
    }
  }

  // When switching folders, fetch that folder's messages on-demand.
  // This prevents Sent from being empty when only Inbox has been loaded.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (selectedFolder === 'sent' || selectedFolder === 'inbox') {
      getMessages({ folder: selectedFolder });
    }
  }, [selectedFolder]);

  // Handle refresh button - fetch fresh data from server
  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch fresh messages and users from server
      console.log("Refresh: calling getUsers")
      await getUsers({ manageLoading: false });
      console.log("Refresh: calling getMessages (inbox)")
      await getMessages({ folder: 'inbox', manageLoading: false });
      console.log("Refresh: calling getMessages (sent)")
      await getMessages({ folder: 'sent', manageLoading: false });
      console.log("Refresh: calling getUserScripts")
      await getUserScripts({ manageLoading: false });
      console.log("Refresh: done")
    } catch (e) {
      console.error('Error refreshing data:', e);
      setError('Failed to refresh data from server');
    } finally {
      setLoading(false);
    }
  };

  const postHandler = async (obj) => {
      setLoading(true);
      setError(null);

      // console.log("endpoint: " + obj.endpoint + ", messageId: " + obj.messageId + ", header: " + obj.header + ", body: " + obj.body);

      let requestBody;
      let headers = { 'Content-Type': 'application/x-www-form-urlencoded' };

      if (obj.body) {
          // For 'send' endpoint, send JSON body
          requestBody = obj.body;
          console.log(obj.body)
          headers = { 'Content-Type': 'application/json' };
      } else {
          // For other endpoints, send form data
          const formDataParams = new URLSearchParams();
          formDataParams.append('endpoint', obj.endpoint);
          formDataParams.append('msgid', obj.messageId);
          formDataParams.append('folder', obj.folder);
          requestBody = formDataParams;
      }      
      

      try {
          const { data } = await axios.post(
              "/api/messages/message_api",
              requestBody,
              {headers}

          );

          if (data) {
              console.log("postHandler " + obj.endpoint);
              // console.log(data);
          } else {
              setError("No response from server");
          }
      } catch (err) {
          console.error("API Error:", err);
          const errorMessage = err.response?.data?.message || "zippity king";
          setError(errorMessage);
          throw err;
      } finally {
          setLoading(false);
      }
  };  
 

  // Compose form state
  const [composeForm, setComposeForm] = useState({
    recipient: '',
    subject: '',
    message: '',
    selectedScript: ''
  });

  // Recipient search state
  const [recipientSearch, setRecipientSearch] = useState('');
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const recipientSearchRef = useRef(null);
  const recipientDropdownRef = useRef(null);

  const readerRef = useRef(null);

  // Filter users based on search term (matches first name, last name, or callsign)
  const getFilteredUsers = useCallback(() => {
    if (!users || !recipientSearch.trim()) return [];
    const term = recipientSearch.toLowerCase().trim();
    const userList = Array.isArray(users) ? users : Object.values(users);
    return userList.filter(user => {
      if (String(user.id) === String(props.currentUserId)) return false;
      return user.name && user.name.toLowerCase().includes(term);
    });
  }, [users, recipientSearch, props.currentUserId]);

  // Close recipient dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        recipientSearchRef.current && !recipientSearchRef.current.contains(e.target) &&
        recipientDropdownRef.current && !recipientDropdownRef.current.contains(e.target)
      ) {
        setShowRecipientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selecting a recipient from search results
  const handleSelectRecipient = (user) => {
    setComposeForm(prev => ({ ...prev, recipient: String(user.id) }));
    setRecipientSearch(user.name);
    setShowRecipientDropdown(false);
  };

  // Handle clearing the selected recipient
  const handleClearRecipient = () => {
    setComposeForm(prev => ({ ...prev, recipient: '' }));
    setRecipientSearch('');
    setShowRecipientDropdown(false);
  };

  // Filter messages based on folder
  const getFilteredMessages = () => {
    let filtered = [];
    
    switch (selectedFolder) {
      case 'inbox':
        filtered = messages.filter(msg => parseInt(msg.recip) === parseInt(props.currentUserId) && parseInt(msg.sent) === 1);
        break;
      case 'sent':
        filtered = messages.filter(msg => parseInt(msg.sender) === parseInt(props.currentUserId) && parseInt(msg.sent) === 1);
        break;
      // case 'drafts':
      //   filtered = messages.filter(msg => parseInt(msg.sender) === parseInt(props.currentUserId) && parseInt(msg.sent) === 0);
      //   break;
      case 'unread':
        filtered = messages.filter(msg => {
          const isUnread = parseInt(msg.recip) === parseInt(props.currentUserId) && parseInt(msg.viewed) === 0 && parseInt(msg.sent) === 1;
          const isFadingOut = fadingOutMessageId === msg.mid;
          // Keep the message in the list if it's unread OR if it's currently fading out
          return isUnread || isFadingOut;
        });
        break;
      default:
        filtered = messages;
    }

    return filtered.sort((a, b) => b.date - a.date);
  };

  const filteredMessages = getFilteredMessages();

  // Get unread count
  const getUnreadCount = () => {
    return messages.filter(msg => parseInt(msg.recip) === parseInt(props.currentUserId) && parseInt(msg.viewed) === 0 && parseInt(msg.sent) === 1).length;
  };

  // Format date
  const formatDate = (epoch) => {
    const epochMs = normalizeEpochToMs(epoch);
    const date = new Date(epochMs);
    const now = new Date();

    const sameDay = date.toDateString() === now.toDateString();
    if (sameDay) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }

    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays >= 0 && diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Handle message selection
  const handleSelectMessage = (msg) => {
    // Ensure date is normalized even if the object came from an old cache.
    setSelectedMessage(normalizeMessage(msg));
    setIsComposing(false);
    setShowMobileReader(true);
    
    // Mark as viewed if it's an unread message
    if (parseInt(msg.recip) === parseInt(props.currentUserId) && parseInt(msg.viewed) === 0) {
      // Only fade out if we're in the unread folder
      if (selectedFolder === 'unread') {
        setFadingOutMessageId(msg.mid);
        // Wait for animation to complete before marking as viewed
        setTimeout(() => {
          markAsViewed(msg.mid);
          setFadingOutMessageId(null);
        }, 500); // Match the CSS animation duration (0.5 seconds)
      } else {
        markAsViewed(msg.mid);
      }
    }
  };

  // Mark message as viewed (optimistic update)
  const markAsViewed = async (messageId) => {
    // Optimistic update
    setMessages(prev => prev.map(msg => 
      msg.mid === messageId ? { ...msg, viewed: 1 } : msg
    ));

    try {
      await postHandler({"endpoint": "mark-viewed", "messageId": messageId, "header":"application/x-www-form-urlencoded"})

    } catch (err) {
      console.error('Error marking message as viewed:', err);
      setError('Failed to mark message as viewed');
      // Revert optimistic update on error
      setMessages(prev => prev.map(msg => 
        msg.mid === messageId ? { ...msg, viewed: 0 } : msg
      ));
    }
  };

  // Handle compose
  const handleCompose = () => {
    setIsComposing(true);
    setSelectedMessage(null);
    setShowMobileReader(true);
    setComposeForm({ recipient: '', subject: '', message: '' });
    setRecipientSearch('');
    setSendEmail(false);
    setShowRecipientDropdown(false);
  };

  // Handle reply
  const handleReply = (msg) => {
    setIsComposing(true);
    setSelectedMessage(null);
    setShowMobileReader(true);
    // Find the sender user to pre-fill the search box
    const senderUser = Array.isArray(users)
      ? users.find(u => String(u.id) === String(msg.sender))
      : users?.[msg.sender];
    setComposeForm({
      recipient: msg.sender.toString(),
      subject: msg.subj.startsWith('RE:') ? msg.subj : `RE: ${msg.subj}`,
      message: ''
    });
    setRecipientSearch(senderUser?.name || '');
    setSendEmail(false);
    setShowRecipientDropdown(false);
  };

  // Handle send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!composeForm.recipient || !composeForm.subject || !composeForm.message) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError(null);

    const newMessage = {
      mid: messages.length + 1,
      date: Date.now(),
      sender: props.currentUserId,
      recip: parseInt(composeForm.recipient),
      subj: composeForm.subject,
      mesg: composeForm.message,
      viewed: 0,
      sent: 1,
      sendEmail: sendEmail,
      endpoint: "send"

    };

    // Optimistic update
    setMessages(prev => [...prev, newMessage]);
    setIsComposing(false);
    setSelectedMessage(newMessage);
    setSelectedFolder('sent');

    try {
      await postHandler({"endpoint": "send", "messageId":-1, "header":"application/json", "body":JSON.stringify(newMessage)});
      // API call would go here
      // const response = await fetch(`${apiEndpoint}/send`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(newMessage)
      // });
      // if (!response.ok) throw new Error('Failed to send message');
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
      // Revert optimistic update on error
      setMessages(prev => prev.filter(msg => msg.mid !== newMessage.mid));
    } finally {
      setLoading(false);
    }
  };

  // Handle delete message - Open Modal
  const handleDeleteMessage = (messageId) => {
    setMessageToDelete(messageId);
    setShowDeleteModal(true);
    setDeleteProgress(0);
    setIsDeleting(false);
  };

  // Confirm Delete Action
  const confirmDelete = async () => {
    if (!messageToDelete) return;

    setIsDeleting(true);
    
    // Simulate progress for visual feedback
    const interval = setInterval(() => {
      setDeleteProgress(prev => {
        if (prev >= 90) {
          return 90; // Hold at 90 until complete
        }
        return prev + 10;
      });
    }, 100);

    // Wait a moment to show the progress bar starting
    await new Promise(resolve => setTimeout(resolve, 500));

    const messageId = messageToDelete;
    
    // Optimistic update
    const deletedMessage = messages.find(msg => msg.mid === messageId);
    setMessages(prev => prev.filter(msg => msg.mid !== messageId));
    setSelectedMessage(null);

    try {
      await postHandler({"endpoint": "delete", "messageId": messageId, "header":"application/x-www-form-urlencoded"});
      
      // Complete progress
      clearInterval(interval);
      setDeleteProgress(100);
      
      // Close modal after short delay
      setTimeout(() => {
          setShowDeleteModal(false);
          setMessageToDelete(null);
          setIsDeleting(false);
          setDeleteProgress(0);
      }, 500);
    } catch (err) {
      clearInterval(interval);
      console.error('Error deleting message:', err);
      setError('Failed to delete message');
      // Revert optimistic update on error
      setMessages(prev => [...prev, deletedMessage]);
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setMessageToDelete(null);
  };

  // Auto-scroll to top of reader pane
  useEffect(() => {
    if (readerRef.current) {
      readerRef.current.scrollTop = 0;
    }
  }, [selectedMessage, isComposing]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowMobileReader(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className={`private-message-system${props.darkMode ? ' dark' : ''}`}>
      {error && (
        <div className="error-banner" role="alert">
          {error}
          <button onClick={() => setError(null)} aria-label="Dismiss error">×</button>
        </div>
      )}

      {/* Action Bar - Top with folder navigation */}
      <div className="action-bar">
        <div className="folder-tabs">
          <button
            className={`folder-tab ${selectedFolder === 'inbox' ? 'active' : ''}`}
            onClick={() => setSelectedFolder('inbox')}
          >
            📥 Inbox
            {getUnreadCount() > 0 && selectedFolder === 'inbox' && (
              <span className="badge">{getUnreadCount()}</span>
            )}
          </button>
          <button
            className={`folder-tab ${selectedFolder === 'unread' ? 'active' : ''}`}
            onClick={() => setSelectedFolder('unread')}
          >
            ⭐ Unread
          </button>
          <button
            className={`folder-tab ${selectedFolder === 'sent' ? 'active' : ''}`}
            onClick={() => setSelectedFolder('sent')}
          >
            📤 Sent
          </button>
          <button
            className="refresh-button"
            onClick={handleRefresh}
            disabled={loading}
            aria-label="Refresh messages"
          >
            {loading ? '⏳' : '🔄'} Refresh
          </button>
        </div>
        
        <button 
          className="btn-primary btn-compose"
          onClick={handleCompose}
          aria-label="Compose new message"
        >
          ✏️ Compose
        </button>
      </div>

      {/* Main Content - 2 Panel Layout */}
      <div className="main-content">
        {/* Message List (Left Panel) */}
        <div className={`message-list-panel ${showMobileReader ? 'mobile-hidden' : ''}`}>
          <div className="message-list-header">
            <h2>{selectedFolder.charAt(0).toUpperCase() + selectedFolder.slice(1)}</h2>
            <span className="message-count">{filteredMessages.length}</span>
          </div>
          
          <div className="message-list">
            {filteredMessages.length === 0 ? (
              <div className="empty-state">
                <p>No messages found</p>
              </div>
            ) : (
              filteredMessages.map((msg) => {
                const isSelected = selectedMessage?.mid === msg.mid;
                const otherUserId = parseInt(msg.sender) === parseInt(props.currentUserId) ? msg.recip : msg.sender;
                const otherUser = users && Array.isArray(users)
                  ? users.find(user => parseInt(user.id) === parseInt(otherUserId))
                  : users?.[otherUserId];
                const isFadingOut = fadingOutMessageId === msg.mid;
                // Keep unread styling during fade-out animation
                const isUnread = (parseInt(msg.recip) === parseInt(props.currentUserId) && parseInt(msg.viewed) === 0) || isFadingOut;
                
                return (
                  <div
                    key={msg.mid}
                    className={`message-item ${isSelected ? 'selected' : ''} ${isUnread ? 'unread' : ''} ${isFadingOut ? 'fading-out' : ''}`}
                    onClick={() => handleSelectMessage(msg)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectMessage(msg);
                      }
                    }}
                    aria-label={`Message from ${otherUser?.name}: ${msg.subj}`}
                  >
                    <div className="message-item-avatar">
                      {otherUser?.avatar || '?'}
                    </div>
                    <div className="message-item-content">
                      <div className="message-item-header">
                        <span className="message-item-sender">
                          {otherUser?.name || 'Unknown User'}
                        </span>
                        <span className="message-item-date">{formatDate(msg.date)}</span>
                      </div>
                      <div className="message-item-subject">
                        {msg.subj} 
                      </div>
                      <div className="message-item-preview">
                        {msg.mesg.substring(0, 100)}...
                      </div>
                    </div>
                    {isUnread && <div className="unread-indicator"></div>}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Reading Pane (Right Panel) */}
        <div className={`reading-pane ${showMobileReader ? 'mobile-show' : ''}`} ref={readerRef}>
          <button 
            className="mobile-back-button"
            onClick={() => setShowMobileReader(false)}
            aria-label="Back to message list"
          >
            ← Back
          </button>

          {isComposing ? (
            <div className="compose-area">
              <h2>New Message</h2>
              <form onSubmit={handleSendMessage} className="compose-form">
                <div className="form-group">
                  <label htmlFor="recipient">To:</label>
                  <div className="recipient-search-container">
                    <div className="recipient-search-input-wrapper">
                      <input
                        ref={recipientSearchRef}
                        id="recipient"
                        type="text"
                        value={recipientSearch}
                        onChange={(e) => {
                          setRecipientSearch(e.target.value);
                          setShowRecipientDropdown(true);
                          // Clear the selected recipient if user modifies the search text
                          if (composeForm.recipient) {
                            const selectedUser = Array.isArray(users)
                              ? users.find(u => String(u.id) === String(composeForm.recipient))
                              : null;
                            if (selectedUser && e.target.value !== selectedUser.name) {
                              setComposeForm(prev => ({ ...prev, recipient: '' }));
                            }
                          }
                        }}
                        onFocus={() => {
                          if (recipientSearch.trim()) setShowRecipientDropdown(true);
                        }}
                        placeholder="Search by name or callsign..."
                        autoComplete="off"
                        className={composeForm.recipient ? 'has-selection' : ''}
                      />
                      {composeForm.recipient && (
                        <button
                          type="button"
                          className="recipient-clear-btn"
                          onClick={handleClearRecipient}
                          aria-label="Clear recipient"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    {showRecipientDropdown && recipientSearch.trim() && (
                      <div className="recipient-dropdown" ref={recipientDropdownRef}>
                        {getFilteredUsers().length === 0 ? (
                          <div className="recipient-dropdown-empty">No users found</div>
                        ) : (
                          getFilteredUsers().map(user => (
                            <div
                              key={user.id}
                              className="recipient-dropdown-item"
                              onClick={() => handleSelectRecipient(user)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleSelectRecipient(user);
                                }
                              }}
                            >
                              <span className="recipient-dropdown-avatar">{user.avatar || '?'}</span>
                              <span className="recipient-dropdown-name">{user.name}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {/* Hidden required input for form validation */}
                  <input
                    type="hidden"
                    value={composeForm.recipient}
                    required
                  />
                </div>

                <div className="form-group form-group-inline">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                    />
                    <span>Also send as email to this user</span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject:</label>
                  <input
                    id="subject"
                    type="text"
                    value={composeForm.subject}
                    onChange={(e) => setComposeForm(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Enter subject"
                    required
                  />
                </div>
                <div className="form-group">
                  {scripts.length > 0 && (
                    <div>
                    {/* <label htmlFor="message">Scripts</label> */}
                    <select
                      value={composeForm.selectedScript}
                      onChange={(e) => {
                        const selectedMsid = e.target.value;
                        const selectedScript = scripts.find(script => String(script.msid) === selectedMsid);
                        if (selectedScript) {
                          setComposeForm(prev => ({
                            ...prev,
                            selectedScript: selectedMsid,
                            message: selectedScript.mesg
                          }));
                        }
                      }}
                    >
                      <option value="">Select a script (optional)</option>
                      {scripts.map(script => (
                        <option key={script.msid} value={script.msid}>
                          {script.subj}
                        </option>
                      ))}
                    </select>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message:</label>
                  <textarea
                    id="message"
                    value={composeForm.message}
                    onChange={(e) => setComposeForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Type your message here..."
                    rows="10"
                    required
                  />
                </div>

                <div className="compose-actions">
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Sending...' : '📤 Send'}
                  </button>
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={() => {
                      setIsComposing(false);
                      setShowMobileReader(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          ) : selectedMessage ? (
            <div className="message-details">
              <div className="message-header">
                <div className="message-header-top">
                  <h2 className="message-subject">{selectedMessage.subj}</h2>
                  <div className="message-actions">
                    <button 
                      className="btn-icon"
                      onClick={() => handleReply(selectedMessage)}
                      aria-label="Reply to message"
                      title="Reply"
                    >
                      ↩️
                    </button>
                    <button 
                      className="btn-icon"
                      onClick={() => handleDeleteMessage(selectedMessage.mid)}
                      aria-label="Delete message"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div className="message-meta">
                  <div className="message-from">
                    {(() => {
                      // Show the "other person" - if we sent it, show recipient; if we received it, show sender
                      const otherPersonId = parseInt(selectedMessage.sender) === parseInt(props.currentUserId) ? selectedMessage.recip : selectedMessage.sender;
                      const otherPerson = users && Array.isArray(users)
                        ? users.find(user => parseInt(user.id) === parseInt(otherPersonId))
                        : users?.[otherPersonId];
                      const isSentByMe = parseInt(selectedMessage.sender) === parseInt(props.currentUserId);
                      
                      return (
                        <>
                          <div className="avatar-large">
                            {otherPerson?.avatar || '?'}
                          </div>
                          <div className="message-from-details">
                            <div className="sender-name">
                              {isSentByMe ? 'To: ' : 'From: '}{otherPerson?.name || 'Unknown User'}
                            </div>
                            <div className="message-recip">
                              {isSentByMe ? 'Sent' : 'Received'}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="message-date-full">
                    {new Date(selectedMessage.date).toLocaleString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>

              <div className="message-body">
                {selectedMessage.mesg}
              </div>
            </div>
          ) : (
            <div className="empty-reader">
              <div className="empty-reader-content">
                <div className="empty-icon">📭</div>
                <p>Select a message to read</p>
                <p className="empty-subtext">or</p>
                <button className="btn-primary" onClick={handleCompose}>
                  Compose New Message
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{isDeleting ? 'Deleting...' : 'Confirm Deletion'}</h3>
            <p>{isDeleting ? 'Deleting message...' : 'Are you sure you want to delete this message?'}</p>
            
            {isDeleting && (
              <div className="progress-container">
                <div className="progress-bar" style={{ width: `${deleteProgress}%` }}></div>
              </div>
            )}

            <div className="modal-actions">
              {!isDeleting && (
                <>
                  <button className="btn-secondary" onClick={cancelDelete}>Cancel</button>
                  <button className="btn-danger" onClick={confirmDelete}>Delete</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivateMessageSystem;
